// Authentication helper utilities for cross-domain authentication

export const setAuthData = (token, userData) => {
  // Store in localStorage
  localStorage.setItem('userToken', token);
  localStorage.setItem('userData', JSON.stringify(userData));
  
  // Store in sessionStorage for immediate access
  sessionStorage.setItem('userToken', token);
  sessionStorage.setItem('userData', JSON.stringify(userData));
  
  // Set cookies for cross-domain access
  const cookieOptions = 'path=/; max-age=86400; SameSite=None; Secure';
  document.cookie = `token=${token}; ${cookieOptions}`;
  document.cookie = `userData=${JSON.stringify(userData)}; ${cookieOptions}`;
};

export const getAuthData = () => {
  // Try to get from sessionStorage first (faster)
  let token = sessionStorage.getItem('userToken');
  let userData = sessionStorage.getItem('userData');
  
  // Fallback to localStorage
  if (!token) {
    token = localStorage.getItem('userToken');
  }
  if (!userData) {
    userData = localStorage.getItem('userData');
  }
  
  // Parse userData if it exists
  if (userData) {
    try {
      userData = JSON.parse(userData);
    } catch (error) {
      console.error('Error parsing user data:', error);
      userData = null;
    }
  }
  
  return { token, userData };
};

export const clearAuthData = () => {
  // Clear from all storage locations
  localStorage.removeItem('userToken');
  localStorage.removeItem('userData');
  sessionStorage.removeItem('userToken');
  sessionStorage.removeItem('userData');
  
  // Clear cookies
  document.cookie = 'token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = 'userData=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
};

export const isAuthenticated = () => {
  const { token } = getAuthData();
  return !!token;
};

export const redirectToExternalApp = (externalUrl, token, userData) => {
  // Set authentication data before redirect
  setAuthData(token, userData);
  
  // Pass authentication data via URL parameters
  const encodedToken = encodeURIComponent(token);
  const encodedUserData = encodeURIComponent(JSON.stringify(userData));
  
  // Add authentication data to URL parameters
  const separator = externalUrl.includes('?') ? '&' : '?';
  const redirectUrl = `${externalUrl}${separator}auth_token=${encodedToken}&auth_user=${encodedUserData}`;
  
  // Redirect to external application with authentication data
  window.location.href = redirectUrl;
};

// Function to handle cross-domain authentication check
export const checkCrossDomainAuth = () => {
  const { token, userData } = getAuthData();
  
  if (token && userData) {
    // You can add additional validation here
    // For example, verify token with your backend
    return { isAuthenticated: true, user: userData };
  }
  
  return { isAuthenticated: false, user: null };
};

// Function to automatically handle authentication redirect
export const handleAuthRedirect = () => {
  const { isAuthenticated, user } = checkCrossDomainAuth();
  
  if (isAuthenticated && !isTokenExpired()) {
    // User is authenticated, redirect to main page
    return { shouldRedirect: false, user };
  } else {
    // User is not authenticated, redirect to login
    return { 
      shouldRedirect: true, 
      redirectUrl: 'https://ritz-carlton.netlify.app/signin',
      user: null 
    };
  }
};

// Function to get authentication headers for API calls
export const getAuthHeaders = () => {
  const { token } = getAuthData();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Function to validate token expiry (basic implementation)
export const isTokenExpired = () => {
  const { token } = getAuthData();
  if (!token) return true;
  
  try {
    // Basic JWT token expiry check (you might want to use a proper JWT library)
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch (error) {
    console.error('Error checking token expiry:', error);
    return true;
  }
};
